import React from 'react';
import { Box, Typography, Button, Chip, Alert } from '@mui/material';
import { AccessTime, InfoOutlined } from '@mui/icons-material';
import { WelcomeScreenProps } from '../types';

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  survey,
  language,
  onStart,
  estimatedTimeMinutes = 0.5, // Default to 30 seconds
}) => {
  const getLocalizedText = (textRecord: Record<string, string> | string): string => {
    if (typeof textRecord === 'string') return textRecord;
    return textRecord[language] || textRecord['en'] || textRecord[Object.keys(textRecord)[0]] || '';
  };

  const getTimeEstimateText = (): string => {
    const seconds = Math.round(estimatedTimeMinutes * 60);
    if (language === 'no') {
      return seconds < 60
        ? `Estimert tid: ${seconds} sekunder`
        : `Estimert tid: ${Math.round(estimatedTimeMinutes)} minutter`;
    } else {
      return seconds < 60
        ? `Estimated time: ${seconds} seconds`
        : `Estimated time: ${Math.round(estimatedTimeMinutes)} minutes`;
    }
  };

  const getStartButtonText = (): string => {
    return language === 'no' ? 'Start undersøkelse' : 'Start Survey';
  };

  const getPrivacyNoticeText = (): string => {
    return language === 'no'
      ? 'For å beskytte personvernet ditt, vennligst ikke inkluder personlig informasjon som navn, kontaktinformasjon eller sensitive opplysninger i svarene dine. Hvis du har tilbakemeldinger som gjelder andre studenter eller ansatte, vennligst ta det direkte med foreleseren.'
      : 'To protect your privacy, please do not include any personal information such as names, contact details, or sensitive data in your responses. If you have feedback regarding other students or faculty members, please speak directly with your lecturer.';
  };

  const handleStartClick = () => {
    onStart();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        py: 4,
      }}
    >
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        {getLocalizedText(survey.title)}
      </Typography>

      {survey.description && (
        <Typography variant="body1" sx={{ mb: 4, maxWidth: 500, color: 'text.secondary' }}>
          {getLocalizedText(survey.description)}
        </Typography>
      )}

      <Chip
        icon={<AccessTime />}
        label={getTimeEstimateText()}
        variant="outlined"
        color="primary"
        sx={{ mb: 3 }}
      />

      <Alert
        severity="info"
        variant="outlined"
        icon={<InfoOutlined />}
        sx={{ mb: 3, maxWidth: 500, textAlign: 'left' }}
      >
        {getPrivacyNoticeText()}
      </Alert>

      <Button
        variant="contained"
        size="large"
        onClick={handleStartClick}
        aria-label={getStartButtonText()}
        data-testid="start-survey-button"
        sx={{
          minHeight: 48,
          px: 4,
          maxWidth: { xs: '100%', sm: 300 },
        }}
      >
        {getStartButtonText()}
      </Button>
    </Box>
  );
};
